// The conversation screens put together for cloud's host (main.tsx):
// - the Workspace sections Inbox (10), DM (20) and Topics (30), their routes
//   (topics, a space's overview, direct messages, the inbox), the team events
//   they hear and the emoji settings;
// - the Me part: personal direct messages (nav order 60, the last, `/me/dms`), a topic
//   beside an Agent chat, the reference kinds `topic` and `user`, and Me's
//   events of personal direct messages.
//
// Promise: one store of the Topics list's state per call (per app), never a
// module's.
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { createRoute, Outlet, redirect } from "@tanstack/react-router";
import { dmPeersPath } from "@trameria/cloud-api/conversations-shared";
import { m } from "@trameria/cloud-i18n";
import {
  type Api,
  defineScreenPart,
  iconOf,
  inboxNowQuery,
  lazyScreen,
  type ResolvedRef,
  refQuery,
  type ScreenPart,
  useApi,
  withLazy,
} from "@trameria/ui-core";
import type { ComponentType, ReactNode } from "react";
import {
  defineWorkspaceSection,
  useWorkspace,
  type WorkspaceSection,
} from "../workspace";
import { MentionMark } from "./mention-mark";
import {
  dmPeersOf,
  dmsQuery,
  inboxQuery,
  messagesQuery,
  PERSONAL_DMS,
  topicQuery,
  workspaceConvPlace,
} from "./queries";
import { conversationTeamHandlers, personalDmHandlers } from "./realtime";
import { createTopicsStore, TopicsStoreContext } from "./screen-state";

// The screens are loaded when first opened (ui-core's app/lazy.tsx).
const WorkspaceInboxScreen = lazyScreen(
  () => import("./inbox"),
  "WorkspaceInboxScreen",
);
const TopicsScreen = lazyScreen(() => import("./topics"), "TopicsScreen");
const TopicScreen = lazyScreen(() => import("./topics"), "TopicScreen");
const NewTopic = lazyScreen(() => import("./topics"), "NewTopic");
const SpaceOverviewScreen = lazyScreen(
  () => import("./overview"),
  "SpaceOverviewScreen",
);
const EmojiSettings = lazyScreen(
  () => import("./emoji-settings"),
  "EmojiSettings",
);
const WorkspaceDmsScreen = lazyScreen(
  () => import("./workspace-dms"),
  "WorkspaceDmsScreen",
);
const WorkspaceNewDm = lazyScreen(
  () => import("./workspace-dms"),
  "WorkspaceNewDm",
);
const WorkspaceDmConversation = lazyScreen(
  () => import("./workspace-dms"),
  "WorkspaceDmConversation",
);
const PersonalDmsScreen = lazyScreen(
  () => import("./personal"),
  "PersonalDmsScreen",
);
const PersonalNewDm = lazyScreen(() => import("./personal"), "PersonalNewDm");
const PersonalDmConversation = lazyScreen(
  () => import("./personal"),
  "PersonalDmConversation",
);
const TopicSideView = lazyScreen(() => import("./personal"), "TopicSideView");
const TopicByIdMissing = lazyScreen(
  () => import("./personal"),
  "TopicByIdMissing",
);

interface Ctx {
  api: Api;
  queryClient: QueryClient;
}

const str = (v: unknown) => (typeof v === "string" && v !== "" ? v : null);

/** Where a topic opens, from what the refs part answered beyond its id. */
export function topicPath(
  id: string,
  path?: Readonly<Record<string, unknown>>,
): string {
  const ws = str(path?.workspace);
  const key = str(path?.key);
  return ws && key ? `/${ws}/topics/${key}` : `/me/topics/${id}`;
}

function Dot({ label }: { label: string }) {
  return (
    <span
      role="img"
      aria-label={label}
      className="inline-block h-[7px] w-[7px] rounded-full bg-accent"
    />
  );
}

/** The workspace's Inbox: something here, or of my own, waits. */
function useInboxBadge(): ReactNode {
  const { slug } = useWorkspace();
  const api = useApi();
  const here = useQuery(inboxQuery(api, slug)).data ?? [];
  const mine = useQuery(inboxNowQuery(api)).data ?? [];
  return here.length > 0 || mine.some((e) => e.place === null) ? (
    <Dot label={m.cv_unread_dot()} />
  ) : null;
}

function useDmsBadge(): ReactNode {
  const { slug } = useWorkspace();
  const list =
    useQuery(dmsQuery(useApi(), workspaceConvPlace(slug))).data ?? [];
  return list.some((t) => t.unread > 0) ? (
    <Dot label={m.cv_unread_dot()} />
  ) : null;
}

/** The nav's dot: a personal direct message has something unread. */
function usePersonalDmsBadge(): ReactNode {
  const list = useQuery(dmsQuery(useApi(), PERSONAL_DMS)).data ?? [];
  return list.some((t) => t.unread > 0) ? (
    <Dot label={m.cv_unread_dot()} />
  ) : null;
}

export interface ConversationsScreen {
  sections: readonly WorkspaceSection[];
  /** For `createMeApp({ parts })`. */
  mePart: ScreenPart;
  /** Whether a path of Me shows a workspace's conversation (the team stream must run). */
  wantsTeamStream(path: string): boolean;
}

export function conversationsScreen(): ConversationsScreen {
  const store = createTopicsStore();
  const withStore = (Screen: ComponentType) =>
    function WithStore() {
      return (
        <TopicsStoreContext.Provider value={store}>
          <Screen />
        </TopicsStoreContext.Provider>
      );
    };

  const inbox = defineWorkspaceSection({
    id: "inbox",
    order: 10,
    label: () => m.cv_inbox(),
    icon: iconOf("inbox"),
    to: "/inbox",
    mine: true,
    useBadge: useInboxBadge,
    routes: (ws) => [
      createRoute({
        getParentRoute: () => ws,
        path: "inbox",
        component: WorkspaceInboxScreen,
      }),
    ],
  });

  const dms = defineWorkspaceSection({
    id: "dms",
    order: 20,
    label: () => m.cv_dms(),
    icon: iconOf("direct"),
    to: "/dms",
    feature: "dm",
    useBadge: useDmsBadge,
    routes: (ws) => {
      const list = createRoute({
        getParentRoute: () => ws,
        path: "dms",
        component: withLazy(function WorkspaceDmsRoute() {
          return (
            <WorkspaceDmsScreen>
              <Outlet />
            </WorkspaceDmsScreen>
          );
        }, WorkspaceDmsScreen),
      });
      return [
        list.addChildren([
          // Nothing open: the list is alone and no detail is drawn.
          createRoute({ getParentRoute: () => list, path: "/" }),
          createRoute({
            getParentRoute: () => list,
            path: "new",
            component: WorkspaceNewDm,
          }),
          createRoute({
            getParentRoute: () => list,
            path: "$userId",
            component: WorkspaceDmConversation,
          }),
        ]),
      ];
    },
  });

  const topics = defineWorkspaceSection({
    id: "topics",
    order: 30,
    label: () => m.cv_topics(),
    icon: iconOf("talk"),
    to: "/topics",
    feature: "topic",
    // Opening the workspace lands here (2026-10-17, the person's answer): the
    // one space's overview when there is one, else the topics of all.
    landing: (spaces) => {
      const talk = spaces.filter(
        (s) => s.archivedAt === null && s.features.includes("topic"),
      );
      return talk.length === 1 && talk[0]
        ? `/spaces/${talk[0].slug}`
        : "/topics";
    },
    routes: (ws) => {
      const list = createRoute({
        getParentRoute: () => ws,
        path: "topics",
        component: withLazy(
          withStore(function TopicsRoute() {
            return (
              <TopicsScreen>
                <Outlet />
              </TopicsScreen>
            );
          }),
          TopicsScreen,
        ),
      });
      const one = createRoute({
        getParentRoute: () => list,
        path: "$key",
        // A missing topic is told in the detail, not by stopping the route.
        loader: async ({ context, params }) => {
          const c = context as Ctx;
          const slug = (params as unknown as { slug: string }).slug;
          const place = workspaceConvPlace(slug);
          const topic = await c.queryClient
            .ensureQueryData(topicQuery(c.api, place, params.key))
            .catch(() => null);
          // A direct message reached by its key (a link, search) opens where
          // direct messages live: one path for what is open.
          const peers = topic ? dmPeersOf(topic) : null;
          if (peers)
            throw redirect({
              href: `/${slug}/dms/${dmPeersPath(peers)}`,
              replace: true,
            });
          if (topic)
            await c.queryClient
              .ensureQueryData(messagesQuery(c.api, place, topic.id))
              .catch(() => undefined);
        },
        component: TopicScreen,
      });
      return [
        list.addChildren([
          // Nothing open: the list is alone and no detail is drawn.
          createRoute({ getParentRoute: () => list, path: "/" }),
          createRoute({
            getParentRoute: () => list,
            path: "new",
            component: NewTopic,
          }),
          one,
        ]),
        createRoute({
          getParentRoute: () => ws,
          path: "spaces/$spaceSlug",
          component: withLazy(
            withStore(SpaceOverviewScreen),
            SpaceOverviewScreen,
          ),
        }),
      ];
    },
    realtime: conversationTeamHandlers,
    settings: [
      {
        id: "emoji",
        group: "admin",
        // Between roles and invites (the old web's order).
        order: 60,
        label: () => m.cv_settings_emoji(),
        icon: iconOf("smile"),
        description: () => m.cv_settings_emoji_short(),
        allowed: (can) => can("emoji.create") || can("emoji.manage"),
        Component: EmojiSettings,
      },
    ],
  });

  const mePart = defineScreenPart({
    id: "conversations",
    nav: {
      // The last of Me's: Inbox, Cases, Agents, Documents, People, then DM
      // (the person, 2026-10-30), under a line above the favorites: a
      // workspace thing, not personal data (the person, 2026-11-01).
      order: 60,
      apart: true,
      label: () => m.cv_me_dms(),
      icon: iconOf("direct"),
      to: "/me/dms",
      useBadge: usePersonalDmsBadge,
    },
    routes: (main) => {
      const list = createRoute({
        getParentRoute: () => main,
        path: "dms",
        component: withLazy(function PersonalDmsRoute() {
          return (
            <PersonalDmsScreen>
              <Outlet />
            </PersonalDmsScreen>
          );
        }, PersonalDmsScreen),
      });
      const byId = createRoute({
        getParentRoute: () => main,
        path: "topics/$id",
        loader: async ({ context, params }) => {
          const c = context as Ctx;
          const ref = (await c.queryClient
            .fetchQuery(refQuery(c.api, "topic", params.id))
            .catch(() => null)) as ResolvedRef | null;
          const to = ref?.visible ? topicPath(params.id, ref.path) : null;
          if (to && !to.startsWith("/me/"))
            throw redirect({ href: to, replace: true });
        },
        component: TopicByIdMissing,
      });
      return [
        list.addChildren([
          // Nothing open: the list is alone and no detail is drawn.
          createRoute({ getParentRoute: () => list, path: "/" }),
          createRoute({
            getParentRoute: () => list,
            path: "new",
            component: PersonalNewDm,
          }),
          createRoute({
            getParentRoute: () => list,
            path: "$userId",
            component: PersonalDmConversation,
          }),
        ]),
        byId,
      ];
    },
    realtime: personalDmHandlers,
    favoriteKinds: {
      topic: { icon: iconOf("talk"), to: topicPath },
      user: {
        icon: iconOf("person"),
        chipMark: MentionMark,
        to: (id, path) => {
          const ws =
            typeof path?.workspace === "string" ? path.workspace : null;
          return ws ? `/${ws}/dms/${id}` : `/me/dms/${id}`;
        },
      },
    },
    sideViews: { topic: TopicSideView },
  });

  return {
    sections: [inbox, dms, topics],
    mePart,
    wantsTeamStream: (path) => /^\/me\/agents\/[^/]+\/view\/topic\//.test(path),
  };
}
